import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import "./Tooltip.css";

export interface TooltipProps {
  /** The tooltip text. An empty string disables the tooltip. */
  label: string;
  /** The control being described. Cloned to receive `aria-describedby`. */
  children: ReactNode;
  /** Preferred side. Flips automatically when it would leave the viewport. */
  side?: "top" | "bottom";
  /** Hover open delay in ms. Focus always opens immediately. */
  delay?: number;
  className?: string;
}

interface Position {
  top: number;
  left: number;
  side: "top" | "bottom";
}

/** Gap between the anchor and the tooltip, in px. */
const OFFSET = 6;

/**
 * A hover/focus tooltip. Never shown on touch (DESIGN.md §7), never a
 * pointer target, and always paired with — not a replacement for — the
 * control's own accessible name.
 */
export function Tooltip({
  label,
  children,
  side = "top",
  delay = 400,
  className,
}: TooltipProps) {
  const uid = useId();
  const tooltipId = `${uid}tip`;

  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const show = useCallback(
    (immediate: boolean) => {
      if (label.length === 0) return;
      cancel();
      if (immediate) {
        setOpen(true);
        return;
      }
      timerRef.current = window.setTimeout(() => setOpen(true), delay);
    },
    [cancel, delay, label.length],
  );

  const hide = useCallback(() => {
    cancel();
    setOpen(false);
    setPosition(null);
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  // Escape dismisses without moving focus (WCAG 1.4.13).
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") hide();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, hide]);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const tip = tooltipRef.current;
    if (anchor === null || tip === null) return;

    const rect = anchor.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();

    const fitsAbove = rect.top - tipRect.height - OFFSET > 0;
    const fitsBelow =
      rect.bottom + tipRect.height + OFFSET < window.innerHeight;
    const resolved: Position["side"] =
      side === "top"
        ? fitsAbove || !fitsBelow
          ? "top"
          : "bottom"
        : fitsBelow || !fitsAbove
          ? "bottom"
          : "top";

    const top =
      resolved === "top"
        ? rect.top - tipRect.height - OFFSET
        : rect.bottom + OFFSET;
    const rawLeft = rect.left + rect.width / 2 - tipRect.width / 2;
    const left = Math.max(
      OFFSET,
      Math.min(rawLeft, window.innerWidth - tipRect.width - OFFSET),
    );

    setPosition({ top, left, side: resolved });
  }, [open, side, label]);

  const describedChild = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        "aria-describedby": open ? tooltipId : undefined,
      })
    : children;

  const style: CSSProperties =
    position === null
      ? { top: 0, left: 0, visibility: "hidden" }
      : { top: position.top, left: position.left };

  const classes = [
    "fc-tooltip",
    `fc-tooltip--${position?.side ?? side}`,
    position === null ? null : "is-visible",
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <>
      <span
        ref={anchorRef}
        className="fc-tooltip__anchor"
        onPointerEnter={(event) => {
          if (event.pointerType === "touch") return;
          show(false);
        }}
        onPointerLeave={hide}
        onPointerDown={hide}
        onFocusCapture={() => show(true)}
        onBlurCapture={hide}
      >
        {describedChild}
      </span>

      {open && label.length > 0 && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={tooltipRef}
              id={tooltipId}
              role="tooltip"
              className={classes}
              style={style}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
